import React, { useState, useEffect } from 'react';

const FindAndReplace: React.FC = () => {
  const [text, setText] = useState('');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [options, setOptions] = useState({
    caseSensitive: false,
    wholeWords: false,
    useRegex: false,
    replaceAll: true
  });
  const [matches, setMatches] = useState<number>(0);

  useEffect(() => {
    if (!findText || !text) {
      setMatches(0);
      return;
    }

    try {
      let pattern: RegExp;
      
      if (options.useRegex) {
        const flags = options.caseSensitive ? 'g' : 'gi';
        pattern = new RegExp(findText, flags);
      } else {
        let escapedFind = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        if (options.wholeWords) {
          escapedFind = `\\b${escapedFind}\\b`;
        }
        
        const flags = options.caseSensitive ? 'g' : 'gi';
        pattern = new RegExp(escapedFind, flags);
      }
      
      const matchesArray = text.match(pattern);
      setMatches(matchesArray ? matchesArray.length : 0);
    } catch (error) {
      setMatches(0);
    }
  }, [text, findText, options]);

  const performReplace = () => {
    if (!findText || !text) return text;

    try {
      let pattern: RegExp;
      
      if (options.useRegex) {
        const flags = options.caseSensitive ? (options.replaceAll ? 'g' : '') : (options.replaceAll ? 'gi' : 'i');
        pattern = new RegExp(findText, flags);
      } else {
        let escapedFind = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        if (options.wholeWords) {
          escapedFind = `\\b${escapedFind}\\b`;
        }
        
        const flags = options.caseSensitive ? (options.replaceAll ? 'g' : '') : (options.replaceAll ? 'gi' : 'i');
        pattern = new RegExp(escapedFind, flags);
      }
      
      return text.replace(pattern, replaceText);
    } catch (error) {
      return text;
    }
  };

  const processedText = performReplace();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(processedText);
  };

  const clearText = () => {
    setText('');
  };

  const clearFind = () => {
    setFindText('');
    setReplaceText('');
  };

  const toggleOption = (key: keyof typeof options) => {
    setOptions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Find & Replace</h1>
        <p className="text-gray-600">Find and replace text with support for regex and various options</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Original Text</h3>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter your text here..."
              className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <div className="mt-4 flex gap-2">
              <button 
                onClick={clearText}
                disabled={!text}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
              >
                Clear Text
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Result</h3>
              <button
                onClick={copyToClipboard}
                disabled={!processedText}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 transition-colors"
              >
                Copy Result
              </button>
            </div>
            <textarea
              value={processedText}
              readOnly
              className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50"
              placeholder="Processed text will appear here..."
            />
            {matches > 0 && (
              <div className="mt-2 text-sm text-gray-600">
                Found {matches} match{matches !== 1 ? 'es' : ''}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Find & Replace Options</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Find</label>
                <input
                  type="text"
                  value={findText}
                  onChange={(e) => setFindText(e.target.value)}
                  placeholder="Enter text to find..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Replace with</label>
                <input
                  type="text"
                  value={replaceText}
                  onChange={(e) => setReplaceText(e.target.value)}
                  placeholder="Enter replacement text..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex gap-2">
                <button 
                  onClick={clearFind}
                  disabled={!findText && !replaceText}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
                >
                  Clear Fields
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-700">Options</h4>
              
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.caseSensitive}
                  onChange={() => toggleOption('caseSensitive')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Case Sensitive</span>
                  <p className="text-xs text-gray-500">Match exact case</p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.wholeWords}
                  onChange={() => toggleOption('wholeWords')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Whole Words Only</span>
                  <p className="text-xs text-gray-500">Match complete words only</p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.useRegex}
                  onChange={() => toggleOption('useRegex')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Use Regular Expression</span>
                  <p className="text-xs text-gray-500">Enable regex pattern matching</p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.replaceAll}
                  onChange={() => toggleOption('replaceAll')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Replace All</span>
                  <p className="text-xs text-gray-500">Replace all occurrences (uncheck to replace first only)</p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindAndReplace;
